import React, { useEffect, useState } from 'react';
import MainLayout from '../../layout/MainLayout';
import { api } from '../../api/axios';
import { tableDateFormate } from '../../utils/dateHelper';
import PaginationBar from '../../components/PaginationBar';
import { Flag, Check, X, Eye, Trash2, ExternalLink } from 'lucide-react';

const PAGE_SIZE = 20;

const STATUS_OPTIONS = ['open', 'reviewed', 'actioned', 'dismissed'];
const TYPE_OPTIONS = ['', 'user', 'post', 'poll', 'comment', 'meeting'];

const STATUS_COLORS = {
    open: 'bg-yellow-100 text-yellow-800',
    reviewed: 'bg-blue-100 text-blue-800',
    actioned: 'bg-green-100 text-green-800',
    dismissed: 'bg-gray-100 text-gray-600',
};

// Maps targetType to its GET + DELETE endpoints.
const ENDPOINT_MAP = {
    post: { get: id => `/posts/${id}`, del: id => `/posts/${id}` },
    poll: { get: id => `/polls/${id}`, del: id => `/polls/${id}` },
    user: { get: id => `/users/${id}`, del: id => `/users/${id}` },
    meeting: { get: id => `/meetings/${id}`, del: id => `/meetings/${id}` },
    // Comments need the parent postId too — not reliably available from the
    // report doc. Support view-only if the API returns enough context.
    comment: { get: null, del: null },
};

const postImageBase = (process.env.REACT_APP_API_URL || 'https://api.jambhanilive.com').replace(/\/api\/v1\/?$/, '');

export default function Reports() {
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [statusFilter, setStatusFilter] = useState('open');
    const [typeFilter, setTypeFilter] = useState('');
    const [updating, setUpdating] = useState(null);

    // Target-view modal
    const [viewOpen, setViewOpen] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [viewReport, setViewReport] = useState(null);

    const fetchItems = async (p = page) => {
        setLoading(true);
        setNotFound(false);
        try {
            const params = new URLSearchParams({ page: String(p), pageSize: String(PAGE_SIZE) });
            if (statusFilter) params.set('status', statusFilter);
            if (typeFilter) params.set('targetType', typeFilter);
            const res = await api.get(`/moderation/reports?${params.toString()}`);
            const data = res.data || {};
            setItems(data.items || data.reports || []);
            setTotalItems(data.total ?? 0);
            setTotalPages(data.totalPages ?? Math.ceil((data.total || 0) / PAGE_SIZE) ?? 1);
        } catch (err) {
            if (err?.response?.status === 404) {
                setNotFound(true);
                setItems([]);
            } else {
                console.error('Reports fetch error:', err);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, statusFilter, typeFilter]);

    const updateStatus = async (id, status) => {
        setUpdating(id);
        try {
            await api.patch(`/moderation/reports/${id}`, { status });
            setItems(prev => prev.map(r => (r._id === id ? { ...r, status } : r)));
        } catch (err) {
            console.error('Report status update failed:', err);
            alert('Failed to update status');
        } finally {
            setUpdating(null);
        }
    };

    const openTarget = async (report) => {
        setViewReport(report);
        setViewOpen(true);
        setViewData(null);
        const ep = ENDPOINT_MAP[report.targetType]?.get;
        if (!ep) return;
        setViewLoading(true);
        try {
            const res = await api.get(ep(report.targetId));
            // Pick the right key based on targetType — don't auto-match, since a
            // post object populates `.user` (author) and we'd grab that instead.
            const d = res.data;
            let extracted;
            switch (report.targetType) {
                case 'user':    extracted = d?.user || d; break;
                case 'post':    extracted = d?.post || d; break;
                case 'poll':    extracted = d?.poll || d; break;
                case 'meeting': extracted = d?.meeting || d; break;
                default:        extracted = d;
            }
            setViewData(extracted);
        } catch (err) {
            console.error('Target fetch error:', err);
            setViewData({ __error: err?.response?.status === 404 ? 'Not found (already deleted?)' : 'Failed to load' });
        } finally {
            setViewLoading(false);
        }
    };

    const deleteTarget = async (report) => {
        const ep = ENDPOINT_MAP[report.targetType]?.del;
        if (!ep) {
            alert(`Delete not supported for type "${report.targetType}"`);
            return;
        }
        if (!window.confirm(`Delete this ${report.targetType}? This cannot be undone.`)) return;
        try {
            await api.delete(ep(report.targetId));
            // Auto-mark report as actioned after a successful delete.
            await api.patch(`/moderation/reports/${report._id}`, { status: 'actioned' }).catch(() => {});
            setItems(prev => prev.map(r => (r._id === report._id ? { ...r, status: 'actioned' } : r)));
            if (viewOpen && viewReport?._id === report._id) setViewOpen(false);
            alert(`${report.targetType} deleted.`);
        } catch (err) {
            console.error('Delete target error:', err);
            alert(err?.response?.data?.message || 'Failed to delete');
        }
    };

    return (
        <MainLayout title="Reports" subtitle="Reports submitted by users against content or other users">
            <div className="mb-4 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Status:</label>
                    <select
                        value={statusFilter}
                        onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                        className="border rounded px-3 py-1.5 text-sm"
                    >
                        <option value="">All</option>
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Type:</label>
                    <select
                        value={typeFilter}
                        onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
                        className="border rounded px-3 py-1.5 text-sm"
                    >
                        {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t || 'All'}</option>)}
                    </select>
                </div>
                <div className="ml-auto text-sm text-gray-500">{totalItems} total</div>
            </div>

            <div className="bg-white border rounded-2xl shadow-sm overflow-x-auto">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 text-gray-600 uppercase text-xs font-semibold">
                        <tr>
                            <th className="px-4 py-3">When</th>
                            <th className="px-4 py-3">Reporter</th>
                            <th className="px-4 py-3">Target</th>
                            <th className="px-4 py-3">Reason</th>
                            <th className="px-4 py-3">Details</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} className="px-6 py-10 text-center text-gray-400">Loading...</td></tr>
                        ) : notFound ? (
                            <tr>
                                <td colSpan={7} className="px-6 py-10 text-center text-gray-400">
                                    <div className="flex flex-col items-center gap-2">
                                        <Flag size={32} className="text-gray-300" />
                                        <div className="font-medium">Reports API not available</div>
                                        <div className="text-xs">The server endpoint <code>GET /api/v1/moderation/reports</code> isn't deployed.</div>
                                    </div>
                                </td>
                            </tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={7} className="px-6 py-10 text-center text-gray-400">No reports.</td></tr>
                        ) : (
                            items.map(r => {
                                const reporterName = r.reporter?.name || r.reporter?.username || r.reporter?._id || '-';
                                const canView = !!ENDPOINT_MAP[r.targetType]?.get;
                                const canDelete = !!ENDPOINT_MAP[r.targetType]?.del;
                                return (
                                    <tr key={r._id} className="border-b hover:bg-gray-50 align-top">
                                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                                            {r.createdAt ? tableDateFormate(r.createdAt) : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm">{reporterName}</td>
                                        <td className="px-4 py-3 text-sm">
                                            <div className="flex flex-col">
                                                <span className="font-medium capitalize">{r.targetType}</span>
                                                <span className="text-xs text-gray-500 font-mono">{String(r.targetId).slice(-8)}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm capitalize">{r.reason || '-'}</td>
                                        <td className="px-4 py-3 text-xs text-gray-500 max-w-xs">
                                            {r.details ? (
                                                <div className="line-clamp-3" title={r.details}>{r.details}</div>
                                            ) : '-'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${STATUS_COLORS[r.status] || 'bg-gray-100'}`}>
                                                {r.status || 'open'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                            <div className="inline-flex gap-1">
                                                {canView && (
                                                    <button
                                                        onClick={() => openTarget(r)}
                                                        className="p-1.5 rounded hover:bg-gray-100 text-gray-700"
                                                        title={`View ${r.targetType}`}
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button
                                                        onClick={() => deleteTarget(r)}
                                                        className="p-1.5 rounded hover:bg-red-50 text-red-600"
                                                        title={`Delete ${r.targetType}`}
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                )}
                                                {r.status !== 'reviewed' && (
                                                    <button
                                                        disabled={updating === r._id}
                                                        onClick={() => updateStatus(r._id, 'reviewed')}
                                                        className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                                                        title="Mark reviewed"
                                                    >
                                                        <Check size={16} />
                                                    </button>
                                                )}
                                                {r.status !== 'dismissed' && (
                                                    <button
                                                        disabled={updating === r._id}
                                                        onClick={() => updateStatus(r._id, 'dismissed')}
                                                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                                                        title="Dismiss"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <div className="mt-4">
                <PaginationBar
                    page={page}
                    pageSize={PAGE_SIZE}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onChange={setPage}
                />
            </div>

            {/* View-target modal */}
            {viewOpen && viewReport && (
                <div
                    className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                    onClick={() => setViewOpen(false)}
                >
                    <div
                        className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-5 py-4 border-b">
                            <div>
                                <div className="text-xs text-gray-500 uppercase tracking-wide">
                                    Reported {viewReport.targetType}
                                </div>
                                <div className="text-sm font-mono text-gray-700">{viewReport.targetId}</div>
                            </div>
                            <button
                                onClick={() => setViewOpen(false)}
                                className="p-2 hover:bg-gray-100 rounded-full"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto p-5">
                            {viewLoading ? (
                                <div className="text-center text-gray-400 py-10">Loading…</div>
                            ) : !viewData ? (
                                <div className="text-center text-gray-400 py-10">No data.</div>
                            ) : viewData.__error ? (
                                <div className="text-center text-red-600 py-10">{viewData.__error}</div>
                            ) : (
                                <TargetPreview type={viewReport.targetType} data={viewData} />
                            )}
                        </div>
                        <div className="border-t px-5 py-3 flex items-center justify-end gap-2">
                            <button
                                onClick={() => setViewOpen(false)}
                                className="px-4 py-2 rounded-lg text-sm hover:bg-gray-100"
                            >
                                Close
                            </button>
                            {ENDPOINT_MAP[viewReport.targetType]?.del && (
                                <button
                                    onClick={() => deleteTarget(viewReport)}
                                    className="px-4 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700 flex items-center gap-2"
                                >
                                    <Trash2 size={14} />
                                    Delete {viewReport.targetType}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </MainLayout>
    );
}

function TargetPreview({ type, data }) {
    if (type === 'post') {
        const images = Array.isArray(data.image) ? data.image : (data.image ? [data.image] : []);
        return (
            <div className="space-y-3">
                <div className="flex items-center gap-3">
                    {data.user?.picture && (
                        <img
                            src={data.user.picture.startsWith('http') ? data.user.picture : `${postImageBase}/${data.user.picture}`}
                            alt="" className="h-10 w-10 rounded-full object-cover"
                        />
                    )}
                    <div>
                        <div className="font-semibold">{data.user?.name || '—'}</div>
                        <div className="text-xs text-gray-500">@{data.user?.username || '—'}</div>
                    </div>
                </div>
                {!!data.description && (
                    <p className="text-sm whitespace-pre-wrap">{data.description}</p>
                )}
                {images.length > 0 && (
                    <div className="grid grid-cols-2 gap-2">
                        {images.map((img, i) => (
                            <a key={i} href={`${postImageBase}/${img}`} target="_blank" rel="noreferrer">
                                <img src={`${postImageBase}/${img}`} alt="" className="rounded-lg w-full object-cover" />
                            </a>
                        ))}
                    </div>
                )}
                <div className="text-xs text-gray-500">Likes: {data.likes ?? 0} • Comments: {data.comments?.length ?? 0}</div>
            </div>
        );
    }
    if (type === 'user') {
        return (
            <div className="space-y-2">
                <div className="flex items-center gap-3">
                    {data.picture && (
                        <img
                            src={data.picture.startsWith('http') ? data.picture : `${postImageBase}/${data.picture}`}
                            alt="" className="h-14 w-14 rounded-full object-cover"
                        />
                    )}
                    <div>
                        <div className="font-semibold text-lg">{data.name}</div>
                        <div className="text-sm text-gray-500">@{data.username}</div>
                    </div>
                </div>
                <div className="text-sm">{data.bio || '—'}</div>
                <div className="text-xs text-gray-500">
                    {data.email || ''} • Followers: {data.followersCount ?? 0} • Following: {data.followingCount ?? 0}
                </div>
            </div>
        );
    }
    if (type === 'poll') {
        return (
            <div className="space-y-2">
                <div className="font-semibold">{data.question}</div>
                <ul className="text-sm list-disc pl-5">
                    {['option1', 'option2', 'option3', 'option4'].map(k => data[k] && (
                        <li key={k}>{data[k]}</li>
                    ))}
                </ul>
                <div className="text-xs text-gray-500">Likes: {data.likes ?? 0}</div>
            </div>
        );
    }
    if (type === 'meeting') {
        return (
            <div className="space-y-2">
                <div className="font-semibold">{data.title}</div>
                <div className="text-sm text-gray-700">{data.description}</div>
                <div className="text-xs text-gray-500">
                    {data.date} • {data.time} • status: {data.status}
                </div>
            </div>
        );
    }
    // Fallback: raw JSON
    return (
        <pre className="text-xs bg-gray-100 p-3 rounded-lg overflow-auto">
{JSON.stringify(data, null, 2)}
        </pre>
    );
}
